/*!
 * @quasar/quasar-ui-qoverlay v2.0.1
 * (c) 2022 Jeff Galbraith <jeff@quasar.dev>
 * Released under the MIT License.
 */

import { getCurrentInstance, defineComponent, onBeforeUnmount, computed, h } from 'vue';
import { useQuasar, colors } from 'quasar';

var listenOpts = {
  hasPassive: false,
  passiveCapture: true,
  notPassiveCapture: true
};

try {
  var opts = Object.defineProperty({}, 'passive', {
    get: function get () {
      Object.assign(listenOpts, {
        hasPassive: true,
        passive: { passive: true },
        notPassive: { passive: false },
        passiveCapture: { passive: true, capture: true },
        notPassiveCapture: { passive: false, capture: true }
      });
    }
  });
  window.addEventListener('qtest', null, opts);
  window.removeEventListener('qtest', null, opts);
}
catch (e) {}

function getEventPath (e) {
  if (e.path) {
    return e.path
  }
  if (e.composedPath) {
    return e.composedPath()
  }

  var path = [];
  var el = e.target;

  while (el) {
    path.push(el);

    if (el.tagName === 'HTML') {
      path.push(document);
      path.push(window);
      return path
    }

    el = el.parentElement;
  }
}

function stopAndPrevent (e) {
  e.cancelable !== false && e.preventDefault();
  e.stopPropagation();
}

function getVerticalScrollPosition (scrollTarget) {
  return scrollTarget === window
    ? window.pageYOffset || window.scrollY || document.body.scrollTop || 0
    : scrollTarget.scrollTop
}

function getHorizontalScrollPosition (scrollTarget) {
  return scrollTarget === window
    ? window.pageXOffset || window.scrollX || document.body.scrollLeft || 0
    : scrollTarget.scrollLeft
}

function hasScrollbar (el, onY) {
  if ( onY === void 0 ) onY = true;

  if (!el || el.nodeType !== Node.ELEMENT_NODE) {
    return false
  }

  return onY
    ? (
        el.scrollHeight > el.clientHeight && (
          el.classList.contains('scroll')
          || el.classList.contains('overflow-auto')
          || [ 'auto', 'scroll' ].includes(window.getComputedStyle(el)[ 'overflow-y' ])
        )
      )
    : (
        el.scrollWidth > el.clientWidth && (
          el.classList.contains('scroll')
          || el.classList.contains('overflow-auto')
          || [ 'auto', 'scroll' ].includes(window.getComputedStyle(el)[ 'overflow-x' ])
        )
      )
}

var
  registered = 0,
  scrollPositionX,
  scrollPositionY,
  maxScrollTop,
  vpPendingUpdate = false,
  bodyLeft,
  bodyTop,
  closeTimer;

function onWheel (e) {
  if (shouldPreventScroll(e)) {
    stopAndPrevent(e);
  }
}

function shouldPreventScroll (e) {
  if (e.target === document.body || e.target.classList.contains('q-layout__backdrop')) {
    return true
  }

  var
    path = getEventPath(e),
    shift = e.shiftKey && !e.deltaX,
    scrollY = !shift && Math.abs(e.deltaX) <= Math.abs(e.deltaY),
    delta = shift || scrollY ? e.deltaY : e.deltaX;

  for (var index = 0; index < path.length; index++) {
    var el = path[ index ];

    if (hasScrollbar(el, scrollY)) {
      return scrollY
        ? (
            delta < 0 && el.scrollTop === 0
              ? true
              : delta > 0 && el.scrollTop + el.clientHeight === el.scrollHeight
          )
        : (
            delta < 0 && el.scrollLeft === 0
              ? true
              : delta > 0 && el.scrollLeft + el.clientWidth === el.scrollWidth
          )
    }
  }

  return true
}

function onAppleScroll (e) {
  if (e.target === document) {
    // required, otherwise iOS blocks further scrolling
    // until the mobile scrollbar dissappears
    document.scrollingElement.scrollTop = document.scrollingElement.scrollTop; // eslint-disable-line
  }
}

function onAppleResize (evt) {
  if (vpPendingUpdate === true) {
    return
  }

  vpPendingUpdate = true;

  requestAnimationFrame(function () {
    vpPendingUpdate = false;

    var ref = evt.target;
    var height = ref.height;
    var ref$1 = document.scrollingElement;
    var clientHeight = ref$1.clientHeight;
    var scrollTop = ref$1.scrollTop;

    if (maxScrollTop === void 0 || height !== window.innerHeight) {
      maxScrollTop = clientHeight - height;
      document.scrollingElement.scrollTop = scrollTop;
    }

    if (scrollTop > maxScrollTop) {
      document.scrollingElement.scrollTop -= Math.ceil((scrollTop - maxScrollTop) / 8);
    }
  });
}

function apply (action, $q) {
  var
    body = document.body,
    hasViewport = window.visualViewport !== void 0;

  if (action === 'add') {
    var ref = window.getComputedStyle(body);
    var overflowY = ref.overflowY;
    var overflowX = ref.overflowX;

    scrollPositionX = getHorizontalScrollPosition(window);
    scrollPositionY = getVerticalScrollPosition(window);
    bodyLeft = body.style.left;
    bodyTop = body.style.top;

    body.style.left = "-" + scrollPositionX + "px";
    body.style.top = "-" + scrollPositionY + "px";

    if (overflowX !== 'hidden' && (overflowX === 'scroll' || body.scrollWidth > window.innerWidth)) {
      body.classList.add('q-body--force-scrollbar-x');
    }
    if (overflowY !== 'hidden' && (overflowY === 'scroll' || body.scrollHeight > window.innerHeight)) {
      body.classList.add('q-body--force-scrollbar-y');
    }

    body.classList.add('q-body--prevent-scroll');
    document.qScrollPrevented = true;
    if ($q.platform.is.ios === true) {
      if (hasViewport === true) {
        window.scrollTo(0, 0);
        window.visualViewport.addEventListener('resize', onAppleResize, listenOpts.passiveCapture);
        window.visualViewport.addEventListener('scroll', onAppleResize, listenOpts.passiveCapture);
        window.scrollTo(0, 0);
      }
      else {
        window.addEventListener('scroll', onAppleScroll, listenOpts.passiveCapture);
      }
    }
  }

  if ($q.platform.is.desktop === true && $q.platform.is.mac === true) {
    // ref. https://developers.google.com/web/updates/2017/01/scrolling-intervention
    window[ (action + "EventListener") ]('wheel', onWheel, listenOpts.notPassive);
  }

  if (action === 'remove') {
    if ($q.platform.is.ios === true) {
      if (hasViewport === true) {
        window.visualViewport.removeEventListener('resize', onAppleResize, listenOpts.passiveCapture);
        window.visualViewport.removeEventListener('scroll', onAppleResize, listenOpts.passiveCapture);
      }
      else {
        window.removeEventListener('scroll', onAppleScroll, listenOpts.passiveCapture);
      }
    }

    body.classList.remove('q-body--prevent-scroll');
    body.classList.remove('q-body--force-scrollbar-x');
    body.classList.remove('q-body--force-scrollbar-y');

    document.qScrollPrevented = false;

    body.style.left = bodyLeft;
    body.style.top = bodyTop;

    window.scrollTo(scrollPositionX, scrollPositionY);
    maxScrollTop = void 0;
  }
}

function preventScroll (state) {
  var vm = getCurrentInstance(),
    $q = useQuasar() || vm.proxy.$q || vm.ctx.$q;

  var action = 'add';

  if (state === true) {
    registered++;

    if (closeTimer !== void 0) {
      clearTimeout(closeTimer);
      closeTimer = void 0;
      return
    }

    if (registered > 1) {
      return
    }
  }
  else {
    if (registered === 0) {
      return
    }

    registered--;

    if (registered > 0) {
      return
    }

    action = 'remove';

    if ($q.platform.is.ios === true && $q.platform.is.nativeMobile === true) {
      clearTimeout(closeTimer);

      closeTimer = setTimeout(function () {
        apply(action, $q);
        closeTimer = void 0;
      }, 100);
      return
    }
  }

  apply(action, $q);
}

var textToRgb = colors.textToRgb;

var QOverlay = defineComponent({
  name: 'QOverlay',
  props: {
    backgroundColor: {
      type: String,
      default: '#000000'
    },
    opacity: {
      type: [Number, String],
      default: 0.35
    },
    zIndex: {
      type: [Number, String],
      default: 6000
    },
    cursorType: {
      type: String,
      default: 'not-allowed'
    },
    noScroll: Boolean,
    modelValue: Boolean
  },
  setup: function setup (props, ref) {
    var slots = ref.slots;

    onBeforeUnmount(function () {
      // make sure noScroll is not left in unintended state
      if (props.noScroll && props.modelValue) {
        preventScroll(false);
      }
    });

    /**
     * Overlay style
     *
     * @return {{padding: number, backgroundColor: string, margin: number, zIndex: (Number|String)}}
     */
    var __style = computed(function () {
      var rgb = textToRgb(props.backgroundColor);
      return {
        zIndex: props.zIndex,
        backgroundColor: ("rgba(" + (rgb.r) + ", " + (rgb.g) + ", " + (rgb.b) + ", " + (props.opacity) + ")"),
        padding: 0,
        margin: 0
      }
    });

    /**
     * Renders component with overlay.
     *
     * @param slot default slot
     * @return h component overlay render function
     * @private
     */
    function __renderComponent (slot) {
      var overlay = h('div', {
        class: ("q-overlay q-overlay--component cursor-" + (props.cursorType)),
        style: __style.value
      }, slots.body());

      return h('div', {
        class: 'q-overlay--wrapper'
      }, [overlay, (slot && slot())])
    }

    /**
     * Renders fullscreen overlay.
     *
     * @return h fullscreen overlay render function
     * @private
     */
    function __renderFullscreen () {
      return h('div', {
        class: ("q-overlay fixed fullscreen cursor-" + (props.cursorType)),
        style: __style.value
      }, [slots.body && slots.body()])
    }

    /**
     * Renders fullscreen overlay or component overlay
     *
     * @return h overlay
     * @private
     */
    function __renderOverlay () {
      var slot = slots.default;
      var isFullscreen = (slot && slot()) === void 0;

      preventScroll(props.noScroll && props.modelValue);

      // nothing to draw when fullscreen and not displaying
      if (!props.modelValue && isFullscreen) {
        return void 0
      }

      if (props.modelValue && isFullscreen) {
        return __renderFullscreen()
      }

      if (props.modelValue && !isFullscreen) {
        return __renderComponent(slot)
      }

      return h('div', {
        class: 'q-overlay'
      }, (slot && slot()) || [])
    }

    return function () { return __renderOverlay(); }
  }
});

var version = '2.0.1';

function install (app) {
  app.component(QOverlay.name, QOverlay);
}

var VuePlugin = /*#__PURE__*/Object.freeze({
  __proto__: null,
  version: version,
  QOverlay: QOverlay,
  install: install
});

export { QOverlay, VuePlugin as default, install, version };
